import { startOfDay } from 'date-fns/startOfDay';
import { endOfDay } from 'date-fns/endOfDay';
import { getMinutes } from 'date-fns/getMinutes';
import { getHours } from 'date-fns/getHours';
import { getYear } from 'date-fns/getYear';
import { getMonth } from 'date-fns/getMonth';
import { format } from 'date-fns/format';
import { addWeeks } from 'date-fns/addWeeks';
import { subWeeks } from 'date-fns/subWeeks';
import { addMonths } from 'date-fns/addMonths';
import { addHours } from 'date-fns/addHours';
import { subHours } from 'date-fns/subHours';
import { addMinutes } from 'date-fns/addMinutes';
import { subMinutes } from 'date-fns/subMinutes';
import { addDays } from 'date-fns/addDays';
import { subDays } from 'date-fns/subDays';
import { subMonths } from 'date-fns/subMonths';
import { subYears } from 'date-fns/subYears';
import { differenceInMinutes } from 'date-fns/differenceInMinutes';
import { differenceInYears } from 'date-fns/differenceInYears';
import { differenceInWeeks } from 'date-fns/differenceInWeeks';
import { differenceInMonths } from 'date-fns/differenceInMonths';
import { differenceInCalendarDays } from 'date-fns/differenceInCalendarDays';
import { intervalToDuration } from 'date-fns/intervalToDuration';
import { isToday } from 'date-fns/isToday';
import { isValid } from 'date-fns/isValid';
import { isAfter } from 'date-fns/isAfter';
import { isBefore } from 'date-fns/isBefore';
import { isEqual } from 'date-fns/isEqual';
import { endOfWeek } from 'date-fns/endOfWeek';
import { startOfWeek } from 'date-fns/startOfWeek';
import { startOfMonth } from 'date-fns/startOfMonth';
import { endOfMonth } from 'date-fns/endOfMonth';
import { isWithinInterval } from 'date-fns/isWithinInterval';
import { areIntervalsOverlapping } from 'date-fns/areIntervalsOverlapping';
import { isSameDay } from 'date-fns/isSameDay';
import { isSameMonth } from 'date-fns/isSameMonth';
import { isSameYear } from 'date-fns/isSameYear';
import { addYears } from 'date-fns/addYears';
import { getDay as getISODay } from 'date-fns/getDay';
import { parseISO } from 'date-fns/parseISO';
// import * as locale from 'date-fns/locale';
import { ru } from 'date-fns/locale/ru';
import { enUS as en } from 'date-fns/locale/en-US';
// import type { Locale as DateFnsLocale } from 'date-fns';
import isNil from 'lodash/isNil';
import isNaN from 'lodash/isNaN';
import mapValues from 'lodash/mapValues';
import i18next from 'i18next';

import { Nullable, Optional } from 'chaika-frontend-types/frontend-cms';

import { Declension } from '@utils/Declension';

import { TimeData } from '@components/TimeSelect';

import { Locale } from 'chaika-enums';
import { getTranslate, translate } from '@chaika/i18n/helper';

// type DateFnsLang = keyof DateFnsLocale;

export type DateType = Date | string | number;

type DateConstructor = {
    year: number | string;
    month: number | string;
    day?: number | string;
    hours?: number | string;
    minutes?: number | string;
    seconds?: number | string;
    milliseconds?: number | string;
};

export interface IntervalParams {
    start: Date;
    end: Date;
}

// https://date-fns.org/v2.8.1/docs/format
export enum DateFormatItem {
    Year = 'yyyy',
    YearShort = 'yy',
    MonthNumber = 'MM',
    MonthStringShort = 'MMM',
    MonthString = 'MMMM',
    MonthStringStandalone = 'LLLL',
    Day = 'dd',
    DayShort = 'd',
    WeekDay = 'EEEEEE',
    Hour = 'H',
    Minute = 'mm',
    Second = 'ss',
}

export enum DateTypes {
    START = 'startDate',
    END = 'endDate',
}

export const enum DateFormat {
    // @ts-ignore H:mm:ss
    Time = `${DateFormatItem.Hour}:${DateFormatItem.Minute}:${DateFormatItem.Second}`,
    // @ts-ignore H:mm
    TimeWithoutSeconds = `${DateFormatItem.Hour}:${DateFormatItem.Minute}`,
    // @ts-ignore dd.MM.yyyy
    DateWithDotSeparator = `${DateFormatItem.Day}.${DateFormatItem.MonthNumber}.${DateFormatItem.Year}`,
    // @ts-ignore dd MMMM yyyy
    DateWithMonthString = `${DateFormatItem.Day} ${DateFormatItem.MonthString} ${DateFormatItem.Year}`,
    // @ts-ignore yyyy-MM-dd
    DateWithDashSeparator = `${DateFormatItem.Year}-${DateFormatItem.MonthNumber}-${DateFormatItem.Day}`,
    // @ts-ignore dd/MM/yy
    DateWithSlashSeparator = `${DateFormatItem.Day}/${DateFormatItem.MonthNumber}/${DateFormatItem.YearShort}`,
    // @ts-ignore dd.MM.yyyy H:mm
    DateWithTime = `${DateFormatItem.Day}.${DateFormatItem.MonthNumber}.${DateFormatItem.Year} ${DateFormatItem.Hour}:${DateFormatItem.Minute}`,
    // @ts-ignore dd.MM.yyyy H:mm
    DateWithTimeAt = `${DateFormatItem.Day}.${DateFormatItem.MonthNumber}.${DateFormatItem.Year} ${translate('at')} ${
        DateFormatItem.Hour
    }:${DateFormatItem.Minute}`,
    // @ts-ignore dd/MM/yyyy H:mm::ss
    DateWithSlashWithFullTime = `${DateFormatItem.Day}/${DateFormatItem.MonthNumber}/${DateFormatItem.Year} ${DateFormatItem.Hour}:${DateFormatItem.Minute}:${DateFormatItem.Second}`,
    // @ts-ignore EEEEEE,d
    WeekDayWithDay = `${DateFormatItem.WeekDay},${DateFormatItem.DayShort}`,
    // @ts-ignore dd MMMM
    DayWithMonthString = `${DateFormatItem.Day} ${DateFormatItem.MonthString}`,
    // @ts-ignore dd MMMM H:mm
    DayWithMonthStringTimeAt = `${DateFormatItem.Day} ${DateFormatItem.MonthString} ${translate('at')} ${
        DateFormatItem.Hour
    }:${DateFormatItem.Minute}`,
    // @ts-ignore dd.MMMM
    DayWithMonth = `${DateFormatItem.Day}.${DateFormatItem.MonthNumber}`,
    // @ts-ignore H:mm, dd.MM.yyyy
    TimeWithoutSecondsWithDate = `${DateFormatItem.Hour}:${DateFormatItem.Minute}, ${DateFormatItem.Day} ${DateFormatItem.MonthString} ${DateFormatItem.Year}`,
}

interface FullAgeData {
    years: number;
    months: number;
    weeks: number;
    days: number;
}

interface FullDateTime {
    year: number;
    month: number;
    day: number;
    hours: number;
    minutes: number;
}

export interface StringDateSeparators {
    dateTime: string;
    date: string;
    time: string;
}

export interface AgeStringParams {
    birthDate: Date;
    currentDate?: Date;
    locale?: Locale;
}

export interface DateOptions {
    dateParser?: string;
    timeParser?: string;
    dateTimeParser?: string;
}

export class DateHelper {
    public static now(): Date {
        return new Date();
    }

    // Нужно для Сафари тк там валидные даты инвалидны YYYY-MM-DD DD-MM-YYYY
    // https://stackoverflow.com/questions/4310953/invalid-date-in-safari
    // Даты в таком формате нам прилетают для записей из медиалога
    public static createDate(value: string): Date {
        return parseISO(value);
    }

    public static parseDate(date: DateConstructor): Date {
        // const { year, month, day, hours, minutes, seconds, milliseconds } = mapValues(date, it => Number(it));
        const { year, month } = mapValues(date, it => Number(it));
        // если в день undefined то получаем инвалидную дату
        return new Date(year, month - 1); // , day, hours, minutes, seconds, milliseconds);
    }

    public static toNoon(value: DateType): Date {
        const date = new Date(value);
        return startOfDay(date);
    }

    public static getTime(date: DateType): string {
        return new Date(date).toLocaleString('ru-RU', { hour: 'numeric', minute: 'numeric' });
    }

    public static toLocalStringDate(value: DateType, withTime?: boolean, withSeconds?: boolean): string {
        const date = new Date(value);

        const options: object = withTime
            ? {
                  year: 'numeric',
                  month: 'numeric',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: 'numeric',
                  second: withSeconds ? 'numeric' : undefined,
              }
            : {
                  year: 'numeric',
                  month: 'numeric',
                  day: 'numeric',
              };

        return date.toLocaleString('ru-RU', options);
    }

    public static toStringDateWithAtParser(value: DateType, options?: DateOptions): string {
        return DateHelper.format(value, DateFormat.DateWithTimeAt);
    }

    public static format(
        value: Optional<Nullable<DateType>>,
        formatParams: DateFormat | DateFormatItem,
        defaultValue: string = '-',
        locale?: Locale,
    ): string {
        let formattedValue = defaultValue;

        if (!isNil(value)) {
            const date = new Date(value);

            if (!isNaN(date.getTime())) {
                const localeDateFns = locale
                    ? locale === Locale.Ru || locale === Locale.Am
                        ? ru
                        : en
                    : DateHelper.getLocale();
                formattedValue = format(date, formatParams as unknown as string, { locale: localeDateFns });
            }
        }

        return formattedValue;
    }

    public static formatIfNotNil(
        value: Optional<Nullable<DateType>>,
        formatParams: DateFormat | DateFormatItem,
    ): Optional<string> {
        let formattedValue;

        if (!isNil(value)) {
            const date = new Date(value);
            const locale = DateHelper.getLocale();

            formattedValue = format(date, formatParams as unknown as string, { locale });
        }

        return formattedValue;
    }

    public static getStartOfDay(value: DateType = new Date()): Date {
        const date = new Date(value);
        return startOfDay(date || new Date());
    }

    public static getEndOfDay(value?: DateType): Date {
        const date = value ? new Date(value) : new Date();
        return endOfDay(date);
    }

    public static normalizeDateRange(startDate?: string, endDate?: string): { startDate?: string; endDate?: string } {
        return {
            startDate: startDate ? DateHelper.getStartOfDay(startDate).toISOString() : startDate,
            endDate: endDate ? DateHelper.getEndOfDay(endDate).toISOString() : endDate,
        };
    }

    public static getMinutes(value: DateType): number {
        return getMinutes(new Date(value));
    }

    public static getHours(value: DateType): number {
        return getHours(new Date(value));
    }

    public static getISODay(value: DateType): number {
        return getISODay(new Date(value));
    }

    public static isWeekend(value: DateType): boolean {
        return this.getISODay(value) === 6 || this.getISODay(value) === 0;
    }

    public static getYear(value?: DateType): number {
        const date = value ? new Date(value) : new Date();
        return getYear(date);
    }

    public static getMonth(value?: DateType): number {
        const date = value ? new Date(value) : new Date();
        return getMonth(date);
    }

    public static addWeeks(value: DateType, count: number): Date {
        return addWeeks(new Date(value), count);
    }

    public static subWeeks(value: DateType, count: number): Date {
        return subWeeks(new Date(value), count);
    }

    public static addMinutes(value: DateType, minutes: number): Date {
        return addMinutes(new Date(value), minutes);
    }

    public static subMinutes(value: DateType, minutes: number): Date {
        return subMinutes(new Date(value), minutes);
    }

    public static addDays(value: DateType, days: number): Date {
        return addDays(new Date(value), days);
    }

    public static addOptionalDays(value: DateType, days?: Nullable<number>): Optional<Date> {
        return !!days ? addDays(new Date(value), days) : undefined;
    }

    public static addMonths(value: DateType, months: number): Date {
        return addMonths(new Date(value), months);
    }

    public static subMonths(value: DateType, months: number): Date {
        return subMonths(new Date(value), months);
    }

    public static addHours(value: DateType, hours: number): Date {
        return addHours(new Date(value), hours);
    }

    public static subHours(value: DateType, hours: number): Date {
        return subHours(new Date(value), hours);
    }

    public static subDays(value: DateType, days: number): Date {
        return subDays(new Date(value), days);
    }

    public static addYears(value: DateType, years: number): Date {
        return addYears(new Date(value), years);
    }

    public static subYears(value: DateType, years: number): Date {
        return subYears(new Date(value), years);
    }

    public static differenceInMinutes(valueLeft: DateType, valueRight: DateType): number {
        const dateLeft = new Date(valueLeft);
        const dateRight = new Date(valueRight);
        return differenceInMinutes(dateLeft, dateRight);
    }

    public static differenceInCalendarDays(valueLeft: DateType, valueRight: DateType): number {
        const dateLeft = new Date(valueLeft);
        const dateRight = new Date(valueRight);
        return differenceInCalendarDays(dateLeft, dateRight);
    }

    // Get the number of full weeks between two dates.
    public static differenceInWeeks(valueLeft: DateType, valueRight: DateType): number {
        const dateLeft = new Date(valueLeft);
        const dateRight = new Date(valueRight);
        return differenceInWeeks(dateLeft, dateRight);
    }

    public static differenceInMonths(valueLeft: DateType, valueRight: DateType): number {
        const dateLeft = new Date(valueLeft);
        const dateRight = new Date(valueRight);
        return differenceInMonths(dateLeft, dateRight);
    }

    public static differenceInYears(valueLeft: DateType, valueRight: DateType): number {
        const dateLeft = new Date(valueLeft);
        const dateRight = new Date(valueRight);
        return differenceInYears(dateLeft, dateRight);
    }

    public static getFullAge(birthDate: DateType, actualOn: DateType = new Date()): FullAgeData {
        const date = new Date(birthDate);
        return {
            years: DateHelper.differenceInYears(actualOn, date),
            months: DateHelper.differenceInMonths(actualOn, date),
            weeks: DateHelper.differenceInWeeks(actualOn, date),
            days: DateHelper.differenceInCalendarDays(actualOn, date),
        };
    }

    public static isAfter(value: DateType, valueToCompare: DateType): boolean {
        const date = new Date(value);
        const dateToCompare = new Date(valueToCompare);
        return isAfter(date, dateToCompare);
    }

    public static isBefore(value: DateType, valueToCompare: DateType): boolean {
        const date = new Date(value);
        const dateToCompare = new Date(valueToCompare);
        return isBefore(date, dateToCompare);
    }

    public static isSameDayOrAfter(value: DateType, valueToCompare: DateType): boolean {
        const date = new Date(value);
        const dateToCompare = new Date(valueToCompare);
        return isSameDay(date, dateToCompare) || isAfter(date, dateToCompare);
    }

    public static isSameDayOrBefore(value: DateType, valueToCompare: DateType): boolean {
        const date = new Date(value);
        const dateToCompare = new Date(valueToCompare);
        return isSameDay(date, dateToCompare) || isBefore(date, dateToCompare);
    }

    public static isSameOrAfter(value: DateType, valueToCompare: DateType): boolean {
        const date = new Date(value);
        const dateToCompare = new Date(valueToCompare);
        return date.getTime() === dateToCompare.getTime() || isAfter(date, dateToCompare);
    }

    public static isSameOrBefore(value: DateType, valueToCompare: DateType): boolean {
        const date = new Date(value);
        const dateToCompare = new Date(valueToCompare);
        return date.getTime() === dateToCompare.getTime() || isBefore(date, dateToCompare);
    }

    public static isInRange(value: DateType, start: DateType, end: DateType): boolean {
        const date = new Date(value);
        const startDate = new Date(start);
        const endDate = new Date(end);

        const isSameOrAfterStart = isEqual(date, startDate) || isAfter(date, startDate);
        const isSameOrBeforeEnd = isEqual(date, endDate) || isBefore(date, endDate);

        return isSameOrAfterStart && isSameOrBeforeEnd;
    }

    public static isToday(value: DateType): boolean {
        const date = new Date(value);
        return isToday(date);
    }

    //checkIsCurrentPeriod = относится к настоящему, это не далекое прошлое и не далекое будущее
    public static isValid(value: DateType, checkIsCurrentPeriod = false): boolean {
        const date = new Date(value);
        let isDateValid = isValid(date);

        if (checkIsCurrentPeriod && isDateValid) {
            isDateValid = this.isAfter(date, subYears(new Date(), 20)) && this.isBefore(date, addYears(new Date(), 5));
        }

        return isDateValid;
    }

    public static isSameDay(valueLeft: DateType, valueRight: DateType): boolean {
        return isSameDay(new Date(valueLeft), new Date(valueRight));
    }

    public static isSameMonth(valueLeft: DateType, valueRight: DateType): boolean {
        return isSameMonth(new Date(valueLeft), new Date(valueRight));
    }

    public static isSameYear(valueLeft: DateType, valueRight: DateType): boolean {
        return isSameYear(new Date(valueLeft), new Date(valueRight));
    }

    public static endOfWeek(value: DateType): Date {
        const date = new Date(value);
        return endOfWeek(date, { weekStartsOn: 1 });
    }

    public static startOfWeek(value: DateType): Date {
        const date = new Date(value);
        return startOfWeek(date, { weekStartsOn: 1 });
    }

    public static endOfMonth(value: DateType = new Date()): Date {
        const date = new Date(value);
        return endOfMonth(date);
    }

    public static startOfMonth(value: DateType): Date {
        const date = new Date(value);
        return startOfMonth(date);
    }

    public static isWithinInterval(value: DateType, interval: IntervalParams): boolean {
        const date = new Date(value);
        return isWithinInterval(date, interval);
    }

    public static areIntervalsOverlapping(interval: IntervalParams, compareInterval: IntervalParams): boolean {
        return areIntervalsOverlapping(interval, compareInterval);
    }

    public static areIntervalOverlappingWithSomeInterval(
        interval: IntervalParams,
        compareIntervals: IntervalParams[],
    ): boolean {
        return compareIntervals.some(compareInterval => this.areIntervalsOverlapping(interval, compareInterval));
    }

    public static isDateIntersectWithIntervals({ start, end }: IntervalParams, date: Date): boolean {
        return this.isSameOrAfter(date, start) && this.isSameOrBefore(date, end);
    }

    public static makeIntervalParams(start: DateType, end: DateType): IntervalParams {
        return {
            start: new Date(start),
            end: new Date(end),
        };
    }

    public static makeTimeData(value: DateType): TimeData {
        const date = new Date(value);

        return {
            hour: date.getHours(),
            minutes: date.getMinutes(),
        };
    }

    public static setTimeData(value: DateType, { hour, minutes }: TimeData): Date {
        const date = new Date(value);
        date.setMinutes(minutes);
        date.setHours(hour);

        return date;
    }

    public static setTime(value: DateType, timeValue: DateType): Date {
        const date = new Date(value);
        const dateTime = new Date(timeValue);

        date.setMinutes(dateTime.getMinutes());
        date.setHours(dateTime.getHours());

        return date;
    }

    public static setDate(
        value: DateType,
        { year, month, day }: { year?: number; month?: number; day?: number },
    ): Date {
        const date = new Date(value);

        if (!isNil(year)) {
            date.setFullYear(year);
        }
        if (!isNil(day)) {
            date.setDate(day);
        }
        if (!isNil(month)) {
            date.setMonth(month);
        }

        return date;
    }

    public static toIsoWithTimezone(value: DateType): string {
        const date = new Date(value);

        return format(date, 'y-MM-dd HH:mm:ssXXX').replace(' ', 'T');
    }

    public static toSimpleDate(value: DateType): string {
        const date = new Date(value);

        return format(date, 'yMMdd');
    }

    public static toHumanElapsedTime(value: DateType): string {
        const getHumanString = (count: number, declensionFn: (v: number) => any): string => {
            const declension = declensionFn(count);
            return `${count} ${declension}`;
        };

        const currentDate = Date.now();
        const date = new Date(value);

        const days = DateHelper.differenceInCalendarDays(currentDate, date);
        const months = DateHelper.differenceInMonths(currentDate, date);
        const years = DateHelper.differenceInYears(currentDate, date);

        const elapsed: string[] = [];

        if (years > 0) {
            elapsed.push(getHumanString(years, Declension.years));
        }

        const remainingMonths: number = months - years * 12;
        if (remainingMonths > 0) {
            elapsed.push(getHumanString(remainingMonths, Declension.months));
        }

        const remainingDays = days - Math.ceil(months * 30.4);
        if (remainingDays > 0 || remainingMonths === 0) {
            elapsed.push(getHumanString(remainingDays, Declension.days));
        }

        return elapsed.slice(0, 2).join(` ${translate('and')} `);
    }

    public static getDurationInDays(endDate: string, startDate: string): string {
        const duration = DateHelper.differenceInCalendarDays(endDate, startDate) + 1;
        return `${duration} ${Declension.days(duration)}`;
    }

    // Длительность между двумя моментами в формате «1 ч. 59 мин. 53 сек.».
    // Нулевые часы и минуты опускаются (короткий интервал — «8 мин. 12 сек.» или «42 сек.»).
    public static formatDuration(from: DateType, to: DateType): string {
        const SECONDS_IN_HOUR = 3600;
        const SECONDS_IN_MINUTE = 60;

        const totalSeconds = Math.max(0, Math.round((new Date(to).getTime() - new Date(from).getTime()) / 1000));

        const hours = Math.floor(totalSeconds / SECONDS_IN_HOUR);
        const minutes = Math.floor((totalSeconds % SECONDS_IN_HOUR) / SECONDS_IN_MINUTE);
        const seconds = totalSeconds % SECONDS_IN_MINUTE;

        const parts: string[] = [];

        if (hours > 0) {
            parts.push(`${hours} ${translate('hoursShort')}`);
        }
        if (hours > 0 || minutes > 0) {
            parts.push(`${minutes} ${translate('minuteShort')}`);
        }
        parts.push(`${seconds} ${translate('secondShort')}`);

        return parts.join(' ');
    }

    public static parseCustomStringToDate(dateString: string, separators: StringDateSeparators): Date {
        const { year, month, day, hours, minutes } = DateHelper.splitDateString(dateString, separators);

        return new Date(year, month - 1, day, hours, minutes);
    }

    public static makeAgeString({ currentDate = new Date(), birthDate, locale = Locale.Ru }: AgeStringParams): string {
        const currentDateWithDay = DateHelper.addDays(currentDate, 1);
        const isEnglishLocale = locale === Locale.En;
        const { years, months, days } = intervalToDuration({ start: new Date(birthDate), end: currentDateWithDay });
        const age = [];
        if (!!years) {
            age.push(`${years} ${isEnglishLocale ? Declension.yearsEng(years) : Declension.years(years)}`);
        }
        if (!!months) {
            age.push(`${months} ${isEnglishLocale ? Declension.monthsEng(months) : Declension.months(months)}`);
        }
        if (!!days) {
            age.push(`${days} ${isEnglishLocale ? Declension.daysEng(days) : Declension.days(days)}`);
        }
        return age.join(' ');
    }

    public static getAge(birthDate: DateType, actualOn: DateType) {
        const currentDateWithDay = DateHelper.addDays(actualOn, 1);
        const { years, months, days } = intervalToDuration({
            start: new Date(birthDate),
            end: new Date(currentDateWithDay),
        });

        return {
            years,
            months,
            days,
        };
    }

    public static formatDateWithYearAwareness(date: DateType): string {
        const isThisYear = DateHelper.isSameYear(date, new Date());

        return isThisYear
            ? DateHelper.format(date, DateFormat.DayWithMonthString)
            : DateHelper.format(date, DateFormat.DateWithMonthString);
    }

    public static makeToDateString(data?: DateType, locale?: Locale): string {
        return data
            ? `${getTranslate('to', locale)} ${this.formatDateWithYearAwareness(data)}`
            : getTranslate('indefinitely', locale);
    }

    private static splitDateString(dateString: string, _: StringDateSeparators): FullDateTime {
        const [date, time] = dateString.split(' ');
        const [day, month, year] = date.split('.');
        const [hours, minutes] = time.split(':');

        return {
            year: Number(year),
            month: Number(month),
            day: Number(day),
            hours: Number(hours),
            minutes: Number(minutes),
        };
    }

    private static getLocale() {
        // const lang: string = i18next.language === Locale.En ? 'enUS' : (i18next.language as DateFnsLang);

        return i18next.language === Locale.Ru ? ru : en;
    }
}
